import styled, { keyframes } from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'
import useLocale from '../../../hooks/useLocale'
import { base } from '../../../styles/themes.jsx'
import { media } from '../../../styles/media.jsx'
import getPeriodDateRange from '../../../utils/widget/getPeriodDateRange.js'
import { widgetFont } from '../widgetTypography'

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.7; }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const WidgetTitle = styled.h3`
  color: ${p => p.theme.font.color};
  ${widgetFont.title}
  margin: 0;
  padding: ${base.space[4]} ${base.space[4]} 0;
  flex-shrink: 0;
`

const BoxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: ${base.space[4]};
  padding: ${base.space[4]};
  overflow: auto;

  ${media.phone} {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`

const TotalBox = styled.div`
  background: ${p => p.theme.card.background};
  box-shadow: ${p => p.theme.card.boxShadow};
  border: 1px solid ${p => p.theme.table.border};
  border-radius: 8px;
  padding: ${base.space[4]};
  display: flex;
  flex-direction: column;
  gap: ${base.space[2]};
  ${p => p.$clickable && `
    cursor: pointer;
    transition: box-shadow 0.15s, border-color 0.15s;
    &:hover {
      box-shadow: ${p.theme.card.boxShadow}, 0 0 0 2px ${p.theme.table.border};
      border-color: ${p.theme.font.label};
    }
  `}

  ${media.phone} {
    flex: 0 0 160px;
  }
`

const BoxTitle = styled.div`
  color: ${p => p.theme.font.label};
  ${widgetFont.label}
`

const MainValue = styled.div`
  color: ${p => p.theme.font.color};
  ${widgetFont.value}
`

const DeltaBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  ${widgetFont.badge}
  width: fit-content;
  background: ${({ $positive, theme }) => ($positive ? theme.emotive.success : theme.emotive.error).bg};
  color: ${({ $positive, theme }) => ($positive ? theme.emotive.success : theme.emotive.error).color};
  border: 1px solid ${({ $positive, theme }) => ($positive ? theme.emotive.success : theme.emotive.error).border};
`

const PrevValue = styled.div`
  color: ${p => p.theme.font.label};
  ${widgetFont.caption}
`

const SkeletonBox = styled.div`
  background: ${p => p.theme.table.border};
  border-radius: 8px;
  height: 80px;
  animation: ${pulse} 1.5s ease-in-out infinite;

  ${media.phone} {
    flex: 0 0 160px;
  }
`

const TotalWidget = ({ data, labels }) => {
  const navigate = useNavigate()
  const { getMe } = useApi()
  const { t } = useTranslation()
  const { lf } = useLocale()

  const boxes = data?.boxes || []
  const uniqueKeys = [...new Set(boxes.map(b => b.statsKey))]

  const queries = uniqueKeys.map(statsKey => {
    const boxesForKey = boxes.filter(b => b.statsKey === statsKey)
    const firstBox = boxesForKey[0]
    const needsPrev = boxesForKey.some(b => b.compareToPrevious)
    const dr = getPeriodDateRange(firstBox.period)
    const from = needsPrev && dr.prevFrom ? dr.prevFrom : dr.from
    const to = dr.to
    let url = `stats/?stat=${statsKey}`
    if (from) url += `&from=${from}&to=${to}`

    return {
      queryKey: ['stats', statsKey, from, to],
      queryFn: () => getMe(url).then(r => r?.data || []),
      keepPreviousData: true,
    }
  })

  const results = useQueries({ queries: queries.length > 0 ? queries : [] })
  const isLoading = results.some(r => r.isLoading)

  const statsMap = Object.fromEntries(uniqueKeys.map((key, i) => [key, results[i]?.data || []]))

  const formatValue = (value, format) => {
    if (format === 'amount') return lf(value, 2, 2)
    if (format === 'integer') return lf(value, 0, 0)
    if (format === 'number') return lf(value, 2, 2)
    return String(value)
  }

  const getBoxValues = (box) => {
    const items = statsMap[box.statsKey] || []
    const dr = getPeriodDateRange(box.period)
    let current = null
    let prev = null

    if (box.period === null) {
      current = items.length === 0 ? null : items.reduce((sum, item) => sum + (item[box.valueField] || 0), 0)
    } else if (box.period === 'currentYear') {
      const currYear = parseInt(dr.currentKey)
      const prevYear = parseInt(dr.prevKey)
      const currItems = items.filter(i => i.year === currYear)
      const prevItems = items.filter(i => i.year === prevYear)
      current = currItems.length > 0 ? currItems.reduce((s, i) => s + (i[box.valueField] || 0), 0) : null
      prev = prevItems.length > 0 ? prevItems.reduce((s, i) => s + (i[box.valueField] || 0), 0) : null
    } else {
      const currItem = items.find(i => i.key === dr.currentKey)
      current = currItem != null ? (currItem[box.valueField] || 0) : null
      const prevItem = items.find(i => i.key === dr.prevKey)
      prev = prevItem != null ? (prevItem[box.valueField] || 0) : null
    }

    let delta = null
    if (box.compareToPrevious && current != null && prev != null && prev !== 0) {
      delta = Math.round(((current - prev) / Math.abs(prev)) * 1000) / 10
    }

    return { current, prev, delta }
  }

  return (
    <Container>
      {labels?.title && <WidgetTitle>{t(labels.title)}</WidgetTitle>}
      <BoxGrid>
        {boxes.map((box, i) => {
          if (isLoading) return <SkeletonBox key={i} />
          const { current, prev, delta } = getBoxValues(box)
          const hasData = current != null
          return (
            <TotalBox key={i} $clickable={!!box.path} onClick={box.path ? () => navigate(box.path) : undefined}>
              <BoxTitle>{t(box.title)}</BoxTitle>
              <MainValue>{hasData ? formatValue(current, box.format) : '—'}</MainValue>
              {box.compareToPrevious && (hasData || prev != null) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {prev != null && <PrevValue>{formatValue(prev, box.format)}</PrevValue>}
                  {hasData && (delta != null
                    ? <DeltaBadge $positive={delta >= 0}>{delta >= 0 ? `+${delta}%` : `${delta}%`}</DeltaBadge>
                    : <DeltaBadge $positive={false}>—</DeltaBadge>
                  )}
                </div>
              )}
            </TotalBox>
          )
        })}
      </BoxGrid>
    </Container>
  )
}

export default TotalWidget
